import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';

// Create the toast context
export const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: 'info', // 'info', 'success', 'error'
    duration: 3000, // Default duration in milliseconds
  });

  // Timer reference for auto-hiding the toast
  const timerRef = useRef(null);

  // Hide the toast
  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, visible: false }));
    
    // Clear the timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Clear the timer when component unmounts or when toast changes
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Show a toast message
  const showToast = useCallback((message, type = 'info', duration = 3000) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set the toast state
    setToast({
      visible: true,
      message,
      type,
      duration,
    });

    // Set a timer to hide the toast after the specified duration (unless it's persistent)
    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        hideToast();
      }, duration);
    }
  }, [hideToast]);

  return (
    <ToastContext.Provider
      value={{
        toast,
        showToast,
        hideToast,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};